import { Badge, Button, ButtonLink, Card, Skeleton, Text } from "@backstage/ui";
import { makeStyles } from "@material-ui/core/styles";
import { ReactNode, useId } from "react";

import { PageHero, PageSection, PageShell, StateCard } from "../layout/pageShell";

import type { BundlePageDescriptor, BundleStatusMark } from "./bundleDescriptors";

// One layout for every bundle's owner page, so a colleague finds the same shape on each.
// Anatomy and rules: docs/app/frontend-system.md § Bundle pages.

/** Whether one action is available, and — when it is not — the reason to show the user. */
export type BundleActionState = { enabled: boolean; reason?: string };

const DOT: Record<BundleStatusMark["tone"], string> = {
	ok: "var(--bui-fg-success)",
	progress: "var(--bui-fg-secondary)",
	warning: "var(--bui-fg-warning)",
	danger: "var(--bui-fg-danger)",
	neutral: "var(--bui-fg-secondary)",
};

const useStyles = makeStyles((theme) => ({
	list: {
		display: "grid",
		gridTemplateColumns: "repeat(2, 1fr)",
		gap: theme.spacing(2),
		"@media (max-width: 960px)": { gridTemplateColumns: "1fr" },
	},
	// One instance (AVD) takes the full measure instead of half of it.
	single: { gridTemplateColumns: "1fr" },
	card: {
		display: "grid",
		gap: theme.spacing(1.25),
		alignContent: "start",
		justifyItems: "start",
	},
	head: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		flexWrap: "wrap",
		gap: theme.spacing(1),
		width: "100%",
	},
	// Colour sits on a decorative dot: the BUI status colours fail AA contrast as small text.
	dot: { width: 8, height: 8, borderRadius: "50%", display: "inline-block" },
	// Motion reads as "still working" on a run that takes minutes.
	spinner: {
		width: 10,
		height: 10,
		borderRadius: "50%",
		border: "2px solid var(--bui-fg-disabled)",
		borderTopColor: "var(--bui-fg-primary)",
		animation: "$spin 0.9s linear infinite",
		"@media (prefers-reduced-motion: reduce)": { animation: "none" },
	},
	"@keyframes spin": { to: { transform: "rotate(360deg)" } },
	facts: {
		display: "grid",
		gridTemplateColumns: "minmax(140px, 1fr) 3fr",
		gap: theme.spacing(0.75, 2),
		margin: 0,
		width: "100%",
		"@media (max-width: 640px)": { gridTemplateColumns: "1fr" },
		"& dd": { margin: 0, overflowWrap: "anywhere" },
	},
	mono: { fontFamily: "var(--bui-font-monospace)", wordBreak: "break-all" },
	actions: { display: "flex", flexWrap: "wrap", gap: theme.spacing(1) },
	danger: { color: "var(--bui-fg-danger)" },
	secondary: {
		borderTop: "1px solid var(--bui-border-1)",
		paddingTop: theme.spacing(1.25),
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		flexWrap: "wrap",
		gap: theme.spacing(1),
		width: "100%",
	},
	action: { display: "grid", gap: theme.spacing(0.5), justifyItems: "start" },
	// BUI styles disabled from `data-disabled`, which `aria-disabled` never sets.
	inert: {
		cursor: "default",
		color: "var(--bui-fg-disabled)",
		background: "var(--bui-bg-neutral-1)",
		border: "1px solid var(--bui-border-1)",
		"&[data-hovered]": { background: "var(--bui-bg-neutral-1)" },
	},
	// Prose stays at a readable measure however wide the card is.
	prose: { display: "grid", gap: theme.spacing(1.5), maxWidth: "72ch", "& ul": { margin: 0 } },
	setUp: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		flexWrap: "wrap",
		gap: theme.spacing(2),
	},
	skeleton: { display: "grid", gap: theme.spacing(2.5) },
}));

/** A status as a small badge: a coloured dot, or a spinner while a run is moving the bundle. */
export function StatusBadge(props: Readonly<{ mark: BundleStatusMark }>): ReactNode {
	const classes = useStyles();
	const { label, tone } = props.mark;
	return (
		<Badge
			size="small"
			icon={
				tone === "progress" ? (
					<span className={classes.spinner} aria-hidden="true" />
				) : (
					<span
						className={classes.dot}
						style={{ background: DOT[tone] }}
						aria-hidden="true"
					/>
				)
			}
		>
			{label}
		</Badge>
	);
}

/** The facts list of a card; children are {@link BundleFact}s. */
export function BundleFacts(props: Readonly<{ children: ReactNode }>): ReactNode {
	const classes = useStyles();
	return <dl className={classes.facts}>{props.children}</dl>;
}

/** One label/value row of a card. A rich `children` value wins over `value`. */
export function BundleFact(
	props: Readonly<{ label: string; value?: string; mono?: boolean; children?: ReactNode }>,
): ReactNode {
	const classes = useStyles();
	return (
		<>
			<dt>
				<Text as="span" variant="body-small" color="secondary">
					{props.label}
				</Text>
			</dt>
			<dd>
				{props.children ?? (
					<Text
						as="span"
						variant="body-small"
						className={props.mono ? classes.mono : undefined}
					>
						{props.value}
					</Text>
				)}
			</dd>
		</>
	);
}

/** A live link, or the reason above a focusable `aria-disabled` button that points at it (always secondary: it is inert). */
export function GatedAction(
	props: Readonly<{
		action: BundleActionState;
		label: string;
		href: string;
		variant?: "primary" | "secondary";
		/** A destructive action reads as one: its label takes the danger colour. */
		danger?: boolean;
		external?: boolean;
		"aria-label"?: string;
	}>,
): ReactNode {
	const classes = useStyles();
	const { action, label, href, variant = "secondary", danger, external } = props;
	const reasonId = useId();

	if (!action.enabled) {
		return (
			<div className={classes.action}>
				<Text as="p" variant="body-small" color="secondary" id={reasonId}>
					{action.reason ?? "Not available right now."}
				</Text>
				<Button
					variant="secondary"
					className={classes.inert}
					aria-disabled="true"
					aria-describedby={reasonId}
				>
					{label}
				</Button>
			</div>
		);
	}

	return (
		<ButtonLink
			variant={variant}
			className={danger ? classes.danger : undefined}
			href={href}
			aria-label={props["aria-label"]}
			{...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
		>
			{label}
		</ButtonLink>
	);
}

/** One bundle instance: name and status, then the primary action, facts and the secondary row. */
export function BundleCard(
	props: Readonly<{
		name: string;
		mark?: BundleStatusMark;
		/** The status in a sentence. */
		status?: ReactNode;
		/** Something that needs attention before the actions, e.g. a reclaim warning. */
		alert?: ReactNode;
		primary?: ReactNode;
		body?: ReactNode;
		facts?: ReactNode;
		/** The optional hold control, for a type that has one. */
		hold?: ReactNode;
		/** Remove or Delete. */
		remove?: ReactNode;
	}>,
): ReactNode {
	const classes = useStyles();
	const { name, mark, status, alert, primary, body, facts, hold, remove } = props;
	return (
		<Card>
			<div className={classes.card}>
				<div className={classes.head}>
					<Text as="h3" variant="title-x-small">
						{name}
					</Text>
					{mark ? <StatusBadge mark={mark} /> : null}
				</div>
				{status}
				{alert}
				{primary ? <div className={classes.actions}>{primary}</div> : null}
				{body}
				{facts ? <BundleFacts>{facts}</BundleFacts> : null}
				{hold || remove ? (
					<div className={classes.secondary}>
						{hold}
						{remove}
					</div>
				) : null}
			</div>
		</Card>
	);
}

/** The cards laid out in a responsive grid: two across, one on a tablet. */
export function BundleList(props: Readonly<{ children: ReactNode; single?: boolean }>): ReactNode {
	const classes = useStyles();
	return (
		<div
			className={[classes.list, props.single ? classes.single : ""].filter(Boolean).join(" ")}
		>
			{props.children}
		</div>
	);
}

/** The shape of the page while the first read is in flight: the hero, then two cards. */
function BundleSkeleton(props: Readonly<{ label: string }>): ReactNode {
	const classes = useStyles();
	return (
		<div className={classes.skeleton} role="status" aria-label={props.label}>
			<div className={classes.skeleton}>
				<Skeleton width={120} height={14} />
				<Skeleton width="min(480px, 90%)" height={44} />
				<Skeleton width={200} height={32} />
			</div>
			<div className={classes.list}>
				{[0, 1].map((n) => (
					<Card key={n}>
						<div className={classes.skeleton}>
							<Skeleton width={160} height={20} />
							<Skeleton width="100%" height={14} />
							<Skeleton width="70%" height={14} />
						</div>
					</Card>
				))}
			</div>
		</div>
	);
}

const ENABLED: BundleActionState = { enabled: true };

/** A bundle type's owner page: hero, "Your <noun>", "Set up", "Good to know". Rules: frontend-system.md § Bundle pages. */
export function BundlePage(
	props: Readonly<{
		descriptor: BundlePageDescriptor;
		title: ReactNode;
		/** Status only: never a button. */
		meta?: ReactNode;
		loading?: boolean;
		/** Replaces the list, e.g. a failed read. */
		state?: ReactNode;
		cards: readonly ReactNode[];
		/** Gating for the Set up action; live when omitted. */
		setup?: BundleActionState;
		/** Stands in for the descriptor's empty sentence, e.g. a reclaim notice. */
		emptyBody?: ReactNode;
	}>,
): ReactNode {
	const classes = useStyles();
	const { descriptor, title, meta, loading, state, cards, setup = ENABLED, emptyBody } = props;

	if (loading) {
		return (
			<PageShell>
				<BundleSkeleton label={`Loading ${descriptor.yours.title.toLowerCase()}`} />
			</PageShell>
		);
	}

	const empty = !state && cards.length === 0;
	const showSetUp = !state && !empty;

	let list: ReactNode;
	if (state) {
		list = state;
	} else if (empty) {
		list = (
			<StateCard
				title={descriptor.empty.title}
				action={
					<GatedAction
						action={setup}
						label={descriptor.empty.setupLabel}
						href={descriptor.setup.href}
						variant="primary"
					/>
				}
			>
				{emptyBody ?? descriptor.empty.body}
			</StateCard>
		);
	} else {
		list = <BundleList single={cards.length === 1}>{cards}</BundleList>;
	}

	return (
		<PageShell>
			<PageHero eyebrow={descriptor.hero.eyebrow} title={title} meta={meta} />
			<PageSection eyebrow={descriptor.yours.eyebrow} title={descriptor.yours.title}>
				{list}
			</PageSection>
			{showSetUp ? (
				<PageSection eyebrow={descriptor.setup.eyebrow} title={descriptor.setup.title}>
					<Card>
						<div className={classes.setUp}>
							<div className={classes.prose}>
								<Text as="p">{descriptor.setup.body}</Text>
							</div>
							<GatedAction
								action={setup}
								label={descriptor.setup.label}
								href={descriptor.setup.href}
							/>
						</div>
					</Card>
				</PageSection>
			) : null}
			<PageSection eyebrow="Good to know" title={descriptor.goodToKnow.title}>
				<Card>
					<div className={classes.prose}>{descriptor.goodToKnow.body}</div>
				</Card>
			</PageSection>
		</PageShell>
	);
}
